import { useState } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  Chip,
  Button,
  Accordion,
  AccordionItem,
} from '@heroui/react'

import type { RecommendationLogEntry } from '../services'
import { RecommendationLogsService } from '../services'

import { PerformanceMetrics } from './PerformanceMetrics'
import { SearchCandidatesPreview } from './SearchCandidatesPreview'
import { LogRecommendationVisualizer } from './LogRecommendationVisualizer'
import { RetrievalPoolPreview } from './RetrievalPoolPreview'

interface RecommendationLogItemProps {
  log: RecommendationLogEntry
  className?: string
}

export function RecommendationLogItem({
  log,
  className,
}: RecommendationLogItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString()
  }

  const getCostChip = () => {
    const totalCost =
      log.performance?.tokenUsage?.processing?.estimated_cost_usd?.total
    if (!totalCost) return null

    return (
      <Chip color="primary" variant="flat" size="sm">
        {RecommendationLogsService.formatCost(totalCost)}
      </Chip>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="text-xl">{log.success ? '✅' : '❌'}</div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  {formatTimestamp(log.timestamp)}
                </span>
              </div>
              <p className="font-mono text-xs text-slate-400">
                {log.requestId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {log.input.refinement?.similarToGifId && (
              <Chip
                variant="flat"
                size="sm"
                className="bg-blue-100 text-blue-800"
              >
                Similar GIFs Request
              </Chip>
            )}
            {getCostChip()}
            <Chip variant="flat" size="sm" color="secondary">
              {RecommendationLogsService.formatProcessingTime(
                log.performance?.processingTimeMs ?? 0
              )}
            </Chip>
            <Button
              size="sm"
              variant="light"
              onPress={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Hide Details' : 'Show Details'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardBody className="pt-0">
        {/* Error Display */}
        {!log.success && log.error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-950 dark:border-red-800">
            <div className="text-sm text-red-700 dark:text-red-300">
              <strong>Error:</strong> {log.error}
            </div>
          </div>
        )}

        {/* Performance Metrics - Always visible when expanded */}
        {isExpanded && (log.performance || log.processing) && (
          <div className="mt-4 space-y-4">
            <PerformanceMetrics
              processingTimeMs={log.performance?.processingTimeMs}
              timingBreakdown={log.performance?.timingBreakdown}
              tokenUsage={log.performance?.tokenUsage}
              documentsEvaluated={
                log.processing?.unifiedAnalysis?.totalDocumentsEvaluated ??
                log.processing?.candidatesFound
              }
              finalRecommendationCount={
                log.processing?.unifiedAnalysis?.finalRecommendationCount ??
                log.results?.recommendationCount
              }
              candidatesFound={log.processing?.candidatesFound}
            />

            {/* Search Candidates Preview */}
            {log.processing?.searchCandidateIds && (
              <SearchCandidatesPreview
                candidateIds={log.processing.searchCandidateIds}
                returnedGifIds={
                  log.results?.recommendations?.map((r) => r.gifId) || []
                }
              />
            )}
          </div>
        )}

        {/* Expandable Details */}
        {isExpanded && (
          <div className="mt-4 space-y-4">
            <Accordion variant="splitted">
              {[
                // Always show these sections
                <AccordionItem
                  key="input"
                  aria-label="Input Details"
                  title="Input Details"
                >
                  <div className="space-y-2 text-sm">
                    {log.input.conversationSnippet && (
                      <div>
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          Conversation Snippet:
                        </span>
                        <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                          {log.input.conversationSnippet}
                        </div>
                      </div>
                    )}
                    {log.input.userIntent && (
                      <div>
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          User Intent:
                        </span>
                        <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                          {log.input.userIntent}
                        </div>
                      </div>
                    )}
                    {log.input.preferences && (
                      <div>
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          Preferences:
                        </span>
                        <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                          {JSON.stringify(log.input.preferences, null, 2)}
                        </div>
                      </div>
                    )}
                    {log.input.refinement && (
                      <div>
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          Refinement:
                        </span>
                        <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                          {JSON.stringify(log.input.refinement, null, 2)}
                        </div>
                        {log.input.refinement.similarToGifId && (
                          <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded text-xs">
                            <span className="font-medium text-blue-700 dark:text-blue-300">
                              Similar GIF Request:
                            </span>
                            <span className="ml-2 font-mono">
                              Target GIF: {log.input.refinement.similarToGifId}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </AccordionItem>,

                // Retrieval pool (stage-A candidates), when present on the log.
                log.processing?.retrievalPool &&
                log.processing.retrievalPool.length > 0 ? (
                  <AccordionItem
                    key="retrieval-pool"
                    aria-label="Retrieval pool"
                    title={`Retrieval pool (${log.processing.retrievalPool.length})`}
                  >
                    <RetrievalPoolPreview
                      pool={log.processing.retrievalPool}
                      returnedGifIds={
                        log.results?.recommendations?.map((r) => r.gifId) ?? []
                      }
                    />
                  </AccordionItem>
                ) : null,

                // Conditionally show results section
                log.success ? (
                  <AccordionItem
                    key="results"
                    aria-label="Results Details"
                    title="Results Details"
                  >
                    <LogRecommendationVisualizer
                      recommendations={log.results?.recommendations ?? []}
                    />
                  </AccordionItem>
                ) : null,
              ].filter(Boolean)}
            </Accordion>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
